import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * @typedef {'string'|'number'|'boolean'|'csv'} UrlStateType
 *
 * Tipos soportados para serializar/deserializar un valor hacia/desde la URL:
 * - `'string'` (default): el valor crudo del querystring tal cual.
 * - `'number'`: `Number(raw)`; si es `NaN` cae al `defaultValue`.
 * - `'boolean'`: `true` si el raw es `'true'` o `'1'`, si no `false`.
 * - `'csv'`: `Array<string>` separado por comas (`a,b,c` → `['a','b','c']`).
 */

/**
 * Decodifica el valor crudo del querystring al tipo pedido.
 * Si `raw` es `null`/`undefined` (param ausente) devuelve `defaultValue`.
 *
 * @param {string|null} raw
 * @param {UrlStateType} type
 * @param {*} defaultValue
 * @returns {*}
 */
function decode(raw, type, defaultValue) {
  if (raw === null || raw === undefined) return defaultValue;
  switch (type) {
    case 'number': {
      const n = Number(raw);
      return Number.isNaN(n) ? defaultValue : n;
    }
    case 'boolean':
      return raw === 'true' || raw === '1';
    case 'csv':
      return raw === ''
        ? []
        : raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    case 'string':
    default:
      return raw;
  }
}

/**
 * Codifica un valor del tipo dado a string para el querystring.
 *
 * @param {*} value
 * @param {UrlStateType} type
 * @returns {string}
 */
function encode(value, type) {
  switch (type) {
    case 'number':
      return String(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'csv':
      return (Array.isArray(value) ? value : []).join(',');
    case 'string':
    default:
      return value == null ? '' : String(value);
  }
}

/**
 * `true` cuando `value` es equivalente al `defaultValue` (para limpiar la URL).
 * Para `csv` compara el contenido del array (mismo orden y elementos); para el
 * resto usa igualdad estricta.
 *
 * @param {*} value
 * @param {*} defaultValue
 * @param {UrlStateType} type
 * @returns {boolean}
 */
function isDefaultValue(value, defaultValue, type) {
  if (type === 'csv') {
    const a = Array.isArray(value) ? value : [];
    const b = Array.isArray(defaultValue) ? defaultValue : [];
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return value === defaultValue;
}

/**
 * Clave estable y serializable para las deps de `useMemo` a partir del default.
 * @param {*} defaultValue
 * @param {UrlStateType} type
 * @returns {string}
 */
function defaultDepKey(defaultValue, type) {
  if (type === 'csv') {
    return Array.isArray(defaultValue) ? defaultValue.join('') : '';
  }
  return String(defaultValue);
}

/**
 * useUrlState — estado persistido en el querystring de la URL, con la misma
 * ergonomía que `useState` pero compartible/bookmarkeable y sincronizado con
 * navegación (back/forward).
 *
 * Es PURA presentación/derivación client-side: envuelve `useSearchParams` de
 * react-router-dom v6 y nunca toca la red. La lectura inicial es SÍNCRONA en el
 * primer render (deriva de `useSearchParams`), así el valor ya está disponible
 * para sembrar el primer fetch del consumidor sin un efecto extra.
 *
 * Reglas:
 * - Por defecto usa `replace` (no ensucia el historial); pasá `push: true` para
 *   navegación tipo tabs donde el back debe volver al tab anterior.
 * - Cuando `value === defaultValue` el parámetro se OMITE de la URL (URL limpia).
 * - Coerción de tipos al leer y escribir según `opts.type`.
 * - `setValue` acepta un valor o una updater fn `(prev) => next` (como useState);
 *   la updater siempre ve el valor MÁS RECIENTE de la URL (sin stale closures).
 * - Liveness: si el componente se desmonta, el setter deja de navegar. Un write
 *   diferido post-unmount (continuación de un fetch, un debounce sin limpiar)
 *   arrastraría al usuario a la pantalla del componente muerto: react-router v6
 *   hereda el pathname del último render en `resolveTo`.
 * - No-op: si el querystring resultante es idéntico al actual (URL viva y router),
 *   no se navega — evita entradas de historial y re-renders por writes sin cambio.
 *
 * @template T
 * @param {string} key  Nombre del parámetro en el querystring.
 * @param {T} defaultValue  Valor usado cuando el param está ausente; también
 *   determina cuándo se omite el param de la URL.
 * @param {Object} [opts]
 * @param {UrlStateType} [opts.type='string']  Tipo para coerción (`csv` ⇒ `string[]`).
 * @param {boolean} [opts.push=false]  `false` ⇒ `replace` (default); `true` ⇒ `push`.
 * @returns {[T, (next: T | ((prev: T) => T)) => void]}  Tupla `[value, setValue]`.
 *
 * @example // (1) filtro string, omitido de la URL cuando vale el default
 * const [status, setStatus] = useUrlState('status', 'all');
 * // setStatus('open')  → ?status=open ; setStatus('all') → URL sin ?status
 *
 * @example // (2) número (paginación), con updater fn
 * const [page, setPage] = useUrlState('page', 1, { type: 'number' });
 * setPage((p) => p + 1); // ?page=2
 *
 * @example // (3) csv ⇒ array<string> (multi-select de tags)
 * const [tags, setTags] = useUrlState('tags', [], { type: 'csv' });
 * setTags(['ruta', 'urgente']); // ?tags=ruta,urgente ; setTags([]) → limpia
 *
 * @example // (4) tab con push:true para que el back vuelva al tab anterior
 * const [tab, setTab] = useUrlState('tab', 'resumen', { push: true });
 */
export function useUrlState(key, defaultValue, opts = {}) {
  const { type = 'string', push = false } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  // Config viva en un ref: mantiene `setValue` con identidad estable y hace que
  // la updater fn lea siempre el default/type/push actuales.
  const cfgRef = useRef();
  cfgRef.current = { key, defaultValue, type, push };

  // Liveness: el querystring pertenece al componente que declara el hook. Si el
  // componente se desmonta, sus setters dejan de navegar (ver reglas del JSDoc).
  // StrictMode monta → limpia → monta sobre el MISMO fiber y los refs sobreviven
  // a ese ciclo: hay que re-fijar `true` dentro del efecto, no solo en la
  // inicialización del ref.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const raw = searchParams.get(key);
  const depKey = defaultDepKey(defaultValue, type);

  // Memo para estabilizar la referencia (importante para `csv`, que devuelve un
  // array nuevo en cada decode). `defaultValue` se omite a propósito de las deps
  // porque su contenido ya está representado por `depKey`.
  const value = useMemo(
    () => decode(raw, type, defaultValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [raw, type, depKey]
  );

  const setValue = useCallback(
    (next) => {
      if (!aliveRef.current) return;
      const { key: k, defaultValue: dv, type: t, push: p } = cfgRef.current;
      // Los params se construyen desde window.location.search (URL VIVA al
      // momento del write), no desde el `prev` que entrega setSearchParams:
      // react-router v6 pasa ahí el searchParams del render que creó el
      // callback, así que dos setters en el mismo tick (reset de varios
      // filtros) o un setter diferido (debounce) se pisaban entre sí
      // (last-write-wins sobre un snapshot viejo). history.pushState/
      // replaceState son síncronos con BrowserRouter ⇒ la lectura es correcta
      // incluso con N writes encadenados en el mismo tick.
      const params = new URLSearchParams(window.location.search);
      const current = decode(params.get(k), t, dv);
      const resolved = typeof next === 'function' ? next(current) : next;
      if (isDefaultValue(resolved, dv, t)) {
        params.delete(k);
      } else {
        params.set(k, encode(resolved, t));
      }
      // No-op: si el querystring resultante ya es exactamente el que ve la URL
      // viva Y el que conoce el router, no navegamos. `searchParams` está fresco
      // aun sin estar en las deps: `setValue` se recrea cuando `setSearchParams`
      // cambia de identidad, y ese cambia en el mismo render que `searchParams`.
      const nextSearch = params.toString();
      if (
        nextSearch === new URLSearchParams(window.location.search).toString() &&
        nextSearch === searchParams.toString()
      ) {
        return;
      }
      setSearchParams(params, { replace: !p });
    },
    // `searchParams` se omite de las deps a propósito: cambia de identidad en el
    // mismo render que `setSearchParams`, así que nunca queda stale aquí.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  return [value, setValue];
}

/**
 * @typedef {Object} UrlStateFieldConfig
 * @property {UrlStateType} [type='string']  Tipo del campo para coerción.
 * @property {*} [defaultValue]  Valor por defecto; cuando el valor lo iguala, el
 *   parámetro se omite de la URL.
 */

/**
 * useUrlStateBatch — actualiza VARIOS parámetros del querystring en una sola
 * navegación (una sola entrada de historial / un solo re-render de la URL).
 *
 * Útil para "aplicar filtros" o resetear varios campos de golpe sin disparar N
 * navegaciones encadenadas (que con `useUrlState` individual podrían pisarse).
 *
 * Devuelve un setter `setMany(updates, callOpts?)`:
 * - `updates` puede ser un objeto parcial `{ [key]: value }` (solo se tocan las
 *   keys presentes; el resto del querystring queda intacto) o una updater fn
 *   `(currentValues) => ({ [key]: value })`, donde `currentValues` trae las keys
 *   del `schema` decodificadas a su tipo.
 * - cada key se setea o se OMITE de la URL si iguala su `defaultValue`.
 * - `callOpts.push` (o el `opts.push` del hook) controla `push` vs `replace`.
 *
 * @param {Object.<string, UrlStateFieldConfig>} [schema={}]  Mapa key→config.
 * @param {Object} [opts]
 * @param {boolean} [opts.push=false]  Default de push/replace para el setter.
 * @returns {(updates: Object | ((current: Object) => Object), callOpts?: {push?: boolean}) => void}
 *
 * @example
 * const setFilters = useUrlStateBatch({
 *   status: { type: 'string', defaultValue: 'all' },
 *   page:   { type: 'number', defaultValue: 1 },
 *   tags:   { type: 'csv',    defaultValue: [] },
 * });
 * // Aplica filtro + reset de página en UNA sola navegación:
 * setFilters({ status: 'open', page: 1, tags: ['urgente'] });
 * // → ?status=open&tags=urgente   (page=1 se omite por ser el default)
 *
 * @example // updater fn que ve los valores actuales decodificados
 * setFilters((cur) => ({ page: cur.page + 1 }));
 */
export function useUrlStateBatch(schema = {}, opts = {}) {
  const { push = false } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  const ref = useRef();
  ref.current = { schema, push };

  // Liveness: mismo guard que useUrlState (ver reglas del JSDoc). StrictMode
  // monta → limpia → monta sobre el MISMO fiber, por eso el efecto re-fija `true`.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  return useCallback(
    (updates, callOpts = {}) => {
      if (!aliveRef.current) return;
      const { schema: sc, push: defPush } = ref.current;
      const usePush = callOpts.push !== undefined ? callOpts.push : defPush;
      // URL viva (ver nota en setValue de useUrlState): el `prev` del updater
      // de setSearchParams es un snapshot del render, no la URL actual.
      const params = new URLSearchParams(window.location.search);

      let next = updates;
      if (typeof updates === 'function') {
        const current = {};
        Object.keys(sc).forEach((k) => {
          const cfg = sc[k] || {};
          current[k] = decode(params.get(k), cfg.type || 'string', cfg.defaultValue);
        });
        next = updates(current) || {};
      }

      Object.keys(next).forEach((k) => {
        const cfg = sc[k] || {};
        const t = cfg.type || 'string';
        const dv = cfg.defaultValue;
        const v = next[k];
        if (isDefaultValue(v, dv, t)) {
          params.delete(k);
        } else {
          params.set(k, encode(v, t));
        }
      });

      // No-op: idéntico a useUrlState (ver nota ahí).
      const nextSearch = params.toString();
      if (
        nextSearch === new URLSearchParams(window.location.search).toString() &&
        nextSearch === searchParams.toString()
      ) {
        return;
      }
      setSearchParams(params, { replace: !usePush });
    },
    // `searchParams` se omite de las deps a propósito: ver nota en useUrlState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );
}

export default useUrlState;
